/*
 * Brand Hub carousel on Swiper (Figma 13:4731 "Brand carousel"). Phase A1.
 * Dependency: Swiper (MIT; the theme already vendors 11.2.8 as assets/swiper-bundle.min.js + .css and loads it from layout/theme.liquid),
 * use approved by Javi on 2026-10-23 ("Puedes utilizar Swiper JS"). This module is loaded only by sections/nc-brand-hub.liquid.
 *
 * NcBrandSwiper.mount(root, { index, labels }) -> { index, show(i), destroy() }
 *   root   .nc-bsw built by the hub: .swiper > .swiper-wrapper > .swiper-slide* , prev/next buttons, .nc-bsw__dots, pause button
 *   labels { prev, next, pause, play, dot: 'Ir a {name}' } (the hub passes the merchant copy)
 * Fires a bubbling `nc-bsw:change` {index} (1-based) when the USER moves to another brand; programmatic show() is silent.
 *
 * UX decisions:
 *  - variable card sizes (Figma 13:4731, approved by Javi 2026-10-23): a wide landscape ACTIVE card and tall portrait side cards. Swiper
 *    keeps equal slots (slidesPerView 'auto' + centeredSlides: its snapping never depends on a card size) and applyProgress() sizes each
 *    card and shifts the neighbours from slide.progress, so it also morphs while the user drags. Chosen over animating the slide widths
 *    (Swiper would have to re-measure mid-transition) and over a hand-written carousel (drag, a11y, autoplay, pagination come from Swiper).
 *  - rewind (not loop): with 4 brands a loop needs duplicated slides (duplicate ids, duplicate links for screen readers) and shows a
 *    brand twice on wide screens; rewind keeps one DOM node per brand and the last > first jump is a real, announced move.
 *  - autoplay is slow (5.5 s), pauses on hover, keyboard focus, hidden tab and when the stage leaves the viewport, stops for good after
 *    the FIRST interaction anywhere in the hub section (pointerdown / keydown; never resumes, the pause button then goes away; the brand drives the products panel: it must not change under the user's hands) and has a
 *    pause button (WCAG 2.2.2). Never runs under prefers-reduced-motion.
 *  - prefers-reduced-motion: speed 0 (instant change, instant card sizes), no autoplay, no entry animation, no tilt.
 */
(function () {
  var AUTOPLAY_MS = 5500;
  var TILT_Y = 4; // deg, UI Kit-style restraint: <= 4
  var TILT_X = 3;

  // tilt for a pointer at (x, y) inside rect: rotateY follows x, rotateX follows -y; both clamped to +-max
  function tilt(x, y, rect, maxX, maxY) {
    if (!rect || !rect.width || !rect.height) return { x: 0, y: 0 };
    var nx = Math.max(-1, Math.min(1, ((x - rect.left) / rect.width - 0.5) * 2));
    var ny = Math.max(-1, Math.min(1, ((y - rect.top) / rect.height - 0.5) * 2));
    return { x: Math.round(-ny * maxX * 100) / 100 + 0, y: Math.round(nx * maxY * 100) / 100 + 0 };
  }

  function fillName(pattern, name) { return String(pattern || '').replace(/\{name\}/g, name); }

  if (typeof module !== 'undefined' && module.exports) module.exports = { tilt: tilt, fillName: fillName };
  if (typeof window === 'undefined') return;

  var reducedQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var hoverQuery = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;

  function mount(root, opts) {
    opts = opts || {};
    var labels = opts.labels || {};
    var el = root.querySelector('.swiper');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.swiper-slide'));
    var prevBtn = root.querySelector('[data-bsw="prev"]');
    var nextBtn = root.querySelector('[data-bsw="next"]');
    var pauseBtn = root.querySelector('[data-bsw="pause"]');
    var dots = root.querySelector('.nc-bsw__dots');
    var status = root.querySelector('[data-bsw-status]');
    var names = slides.map(function (s) { return s.getAttribute('data-name') || ''; });
    var swiper = null;
    var silent = 0;
    var userStopped = false; // autoplay stopped by the user (interaction or pause button): never restarts by itself
    var inView = true;
    var cleanups = [];
    var api = { index: opts.index || 0, show: show, destroy: destroy, swiper: null };

    function on(target, type, fn, o) {
      target.addEventListener(type, fn, o);
      cleanups.push(function () { target.removeEventListener(type, fn, o); });
    }

    function reduced() { return !!(reducedQuery && reducedQuery.matches); }

    function emit(i) {
      root.dispatchEvent(new CustomEvent('nc-bsw:change', { bubbles: true, detail: { index: i + 1 } }));
    }

    function syncSlides() {
      slides.forEach(function (s, i) {
        var active = swiper ? i === swiper.activeIndex : i === api.index;
        s.setAttribute('data-active', active ? 'true' : 'false');
        if (active) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
        // side cards are a pointer shortcut (click = make active); keyboard users reach brands with arrows and dots
        Array.prototype.forEach.call(s.querySelectorAll('a, button'), function (c) { c.tabIndex = active ? 0 : -1; });
      });
    }

    function playLabel() {
      if (!pauseBtn) return;
      var running = !!(swiper && swiper.autoplay && swiper.autoplay.running) && !userStopped;
      pauseBtn.setAttribute('aria-pressed', running ? 'false' : 'true');
      pauseBtn.setAttribute('aria-label', running ? labels.pause : labels.play);
      pauseBtn.setAttribute('data-state', running ? 'playing' : 'paused');
    }

    function stopAutoplay() {
      userStopped = true;
      if (swiper && swiper.autoplay && swiper.autoplay.running) swiper.autoplay.stop();
      playLabel();
      hidePause();
    }

    // Javi 2026-10-25: once the rotation has stopped (first interaction anywhere in the hub, or the pause button) it never resumes, so the
    // pause control has nothing left to do: it leaves the page (focus goes to the carousel so a keyboard user does not lose their place)
    function hidePause() {
      if (!pauseBtn || pauseBtn.hidden) return;
      var had = document.activeElement === pauseBtn;
      pauseBtn.hidden = true;
      if (had) { var focusable = root.querySelector('.swiper'); if (focusable && focusable.focus) focusable.focus(); }
    }

    function wantAutoplay() { return !reduced() && !userStopped && inView && slides.length > 1 && !document.hidden; }

    function syncAutoplay() {
      if (!swiper || !swiper.autoplay) return;
      if (wantAutoplay()) { if (!swiper.autoplay.running) swiper.autoplay.start(); }
      else if (swiper.autoplay.running) swiper.autoplay.stop();
      playLabel();
    }

    // card sizes live in CSS custom properties on the root (mobile first, see section-nc-brand-hub.css); read once per layout change
    var dims = null;
    function px(name) { return parseFloat(getComputedStyle(root).getPropertyValue(name)) || 0; }
    function readDims() {
      dims = { aw: px('--bsw-act-w'), ah: px('--bsw-act-h'), sw: px('--bsw-side-w'), h: [px('--bsw-act-h'), px('--bsw-side-h1'), px('--bsw-side-h2'), px('--bsw-side-h3')] };
      fitNames(dims);
      return dims;
    }

    // a long brand name (STRADIVARIUS) must stay inside the text area of the landscape active card: shrink it, never below 20 px
    function fitNames(d) {
      var landscape = !window.matchMedia || window.matchMedia('(min-width: 600px)').matches;
      var avail = landscape ? d.aw * 0.46 - px('--bsw-pad') - 8 : d.aw - 2 * px('--bsw-pad');
      var base = px('--bsw-name');
      slides.forEach(function (s, i) {
        var longest = String(names[i]).split(/\s+/).reduce(function (m, w) { return Math.max(m, w.length); }, 1);
        var fit = Math.max(20, Math.min(base, Math.floor(avail / (longest * 0.74))));
        s.style.setProperty('--bsw-name-fit', fit + 'px');
      });
    }

    // Variable card sizes: `progress` is 0 for the centred slide, +1 per slot to its left and -1 per slot to its right. Active-card mix = 1 - |p|; the card is
    // sideW + (activeW - sideW) * mix wide, its height steps down with distance, and neighbours shift away by half the extra width so
    // the gaps stay constant. Swiper sets progress to the FINAL value for animated moves and the CSS transitions (--bsw-t) animate it.
    function applyProgress() {
      if (!swiper || !swiper.slides) return;
      var d = dims || readDims();
      swiper.slides.forEach(function (slide) {
        var p = slide.progress || 0;
        var ap = Math.abs(p);
        var mix = Math.max(0, 1 - ap);
        var k = Math.min(ap, 3);
        var lo = Math.floor(k);
        var h = d.h[lo] + ((d.h[Math.min(lo + 1, 3)] - d.h[lo]) * (k - lo));
        var card = slide.firstElementChild;
        var shift = (p > 0 ? -1 : 1) * Math.min(ap, 1) * (d.aw - d.sw) / 2;
        slide.style.transform = 'translate3d(' + Math.round(shift * 100) / 100 + 'px, 0, 0)';
        if (card) {
          card.style.width = Math.round((d.sw + (d.aw - d.sw) * mix) * 100) / 100 + 'px';
          card.style.height = Math.round(h * 100) / 100 + 'px';
        }
      });
    }

    function build(initial) {
      var motion = !reduced();
      var cfg = {
        initialSlide: initial,
        centeredSlides: true,
        slidesPerView: 'auto',
        spaceBetween: 0,
        grabCursor: true,
        rewind: true,
        slideToClickedSlide: true,
        watchSlidesProgress: true,
        speed: motion ? 520 : 0,
        mousewheel: false,
        keyboard: false, // arrows are handled below, only while focus is inside the carousel
        a11y: {
          enabled: true,
          slideLabelMessage: '', // the hub sets "Marca 1 de 4: Edmmond" on each slide itself
          slideRole: 'group',
          prevSlideMessage: labels.prev,
          nextSlideMessage: labels.next
        },
        navigation: { prevEl: prevBtn, nextEl: nextBtn, disabledClass: 'is-disabled' },
        pagination: {
          el: dots,
          clickable: true,
          bulletElement: 'button',
          bulletClass: 'nc-bsw__dot',
          bulletActiveClass: 'nc-bsw__dot--active',
          renderBullet: function (i, cls) {
            return '<button type="button" class="' + cls + '" aria-label="' + fillName(labels.dot, names[i] || String(i + 1)).replace(/"/g, '&quot;') + '"></button>';
          }
        }
      };
      // no Swiper effect: the cards are sized and the neighbours shifted from each slide's progress (applyProgress), see the header
      cfg.on = {
        setTranslate: applyProgress,
        setTransition: function (s, duration) { root.style.setProperty('--bsw-t', duration + 'ms'); },
        resize: function () { dims = null; applyProgress(); },
        update: function () { dims = null; applyProgress(); }
      };
      if (motion) cfg.autoplay = { delay: AUTOPLAY_MS, disableOnInteraction: false, pauseOnMouseEnter: true, waitForTransition: true };
      swiper = new window.Swiper(el, cfg);
      api.swiper = swiper;
      root.style.setProperty('--bsw-t', '0ms');
      applyProgress();
      api.index = swiper.activeIndex;

      swiper.on('slideChange', function () {
        api.index = swiper.activeIndex;
        syncSlides();
        if (silent) return;
        if (status) status.textContent = slides[swiper.activeIndex].getAttribute('aria-label') || '';
        emit(swiper.activeIndex);
      });
      swiper.on('autoplayStop', playLabel);
      swiper.on('autoplayStart', playLabel);
      // a swipe, drag, arrow or dot by the user ends the autoplay for good (Swiper only reports touches; clicks are caught below)
      swiper.on('touchStart', stopAutoplay);
      syncSlides();
      syncAutoplay();
    }

    function show(i) {
      if (!swiper || i === null || i < 0 || i >= slides.length || i === swiper.activeIndex) return;
      silent++;
      swiper.slideTo(i, reduced() ? 0 : 520);
      silent--;
      api.index = i;
      syncSlides();
    }

    function onKeydown(e) {
      var next = { ArrowLeft: swiper.activeIndex - 1, ArrowRight: swiper.activeIndex + 1, Home: 0, End: slides.length - 1 }[e.key];
      if (next === undefined || (e.target.closest && e.target.closest('input, textarea, select'))) return;
      e.preventDefault();
      stopAutoplay();
      var from = swiper.activeIndex;
      var to = Math.max(0, Math.min(slides.length - 1, next));
      if (e.key === 'ArrowLeft' && from === 0) to = slides.length - 1; // rewind, as the arrows do
      if (e.key === 'ArrowRight' && from === slides.length - 1) to = 0;
      swiper.slideTo(to, reduced() ? 0 : 520);
      // on the dots focus follows the active brand (roving, like tabs)
      var bullets = dots ? dots.querySelectorAll('.nc-bsw__dot') : [];
      if (e.target.classList && e.target.classList.contains('nc-bsw__dot') && bullets[to]) bullets[to].focus();
    }

    function wireInteraction() {
      // any press on arrows or dots ends autoplay for good
      on(root, 'click', function (e) {
        if (e.target.closest('[data-bsw="prev"], [data-bsw="next"], .nc-bsw__dot') ) stopAutoplay();
        else if (e.target.closest('.swiper-slide') && !e.target.closest('a')) stopAutoplay();
      });
      on(root, 'keydown', onKeydown);
      // a tap or click (no drag) on a side card makes it active. Done from pointerdown/up on the slide because the browser's click
      // target can fall back to the wrapper when the card moves under the pointer (hover lift in a 3D slide).
      var down = null;
      on(el, 'pointerdown', function (e) {
        var slide = e.target.closest ? e.target.closest('.swiper-slide') : null;
        down = slide ? { i: slides.indexOf(slide), x: e.clientX, y: e.clientY } : null;
      });
      on(el, 'pointerup', function (e) {
        var d = down;
        down = null;
        if (!d || d.i < 0 || d.i === swiper.activeIndex) return;
        if (Math.abs(e.clientX - d.x) > 6 || Math.abs(e.clientY - d.y) > 6) return; // a drag, Swiper handles it
        swiper.slideTo(d.i, reduced() ? 0 : 520);
      });
      // the pause button only stops (WCAG 2.2.2); there is no resume (Javi 2026-10-25)
      if (pauseBtn) on(pauseBtn, 'click', function () { stopAutoplay(); });
      // keyboard focus pauses (a mouse click that leaves focus on a button does not: :focus-visible only)
      on(root, 'focusin', function (e) {
        var visible = false;
        try { visible = e.target.matches(':focus-visible'); } catch (err) { visible = false; }
        if (visible && swiper.autoplay && swiper.autoplay.running) swiper.autoplay.pause();
      });
      on(root, 'focusout', function () {
        if (swiper.autoplay && swiper.autoplay.paused && wantAutoplay() && !root.contains(document.activeElement)) swiper.autoplay.resume();
      });
      on(document, 'visibilitychange', syncAutoplay);
      // working elsewhere on the hub (try-on, products, look panel) ends autoplay for good: the brand must not change under the user's hands
      var screen = root.closest('nc-brand-hub-screen') || document;
      // the first pointerdown / keydown ANYWHERE in the hub section (cards, chips, hearts, the fitting room, the carousel itself) ends it for good
      var firstInteraction = function () { stopAutoplay(); };
      on(screen, 'pointerdown', firstInteraction, true);
      on(screen, 'keydown', firstInteraction, true);
      if (window.IntersectionObserver) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            inView = en.isIntersecting;
            if (en.isIntersecting && root.getAttribute('data-bsw-state') !== 'in') enter();
            syncAutoplay();
          });
        }, { threshold: 0.35 });
        io.observe(root);
        cleanups.push(function () { io.disconnect(); });
      } else {
        enter();
      }
    }

    // entry: cards rise and fade in, staggered (CSS reads --bsw-i), only when motion is allowed
    function enter() {
      root.setAttribute('data-bsw-state', 'in');
    }

    // hover tilt: pointer devices only, one card at a time, rotateX/rotateY <= 4deg written as CSS variables (CSS owns the transform)
    function wireTilt() {
      var current = null;
      var raf = 0;
      var last = null;
      function reset(card) {
        if (!card) return;
        card.style.removeProperty('--bsw-rx');
        card.style.removeProperty('--bsw-ry');
        card.removeAttribute('data-tilting');
      }
      function apply() {
        raf = 0;
        if (!current || !last) return;
        var t = tilt(last.x, last.y, current.getBoundingClientRect(), TILT_X, TILT_Y);
        current.style.setProperty('--bsw-rx', t.x + 'deg');
        current.style.setProperty('--bsw-ry', t.y + 'deg');
      }
      on(el, 'pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !hoverQuery || !hoverQuery.matches || reduced() || (e.buttons & 1)) { if (current) { reset(current); current = null; } return; }
        var card = e.target.closest ? e.target.closest('.nc-bsw__card') : null;
        if (card !== current) { reset(current); current = card; }
        if (!card) return;
        card.setAttribute('data-tilting', 'true');
        last = { x: e.clientX, y: e.clientY };
        if (!raf) raf = window.requestAnimationFrame(apply);
      });
      on(el, 'pointerleave', function () { reset(current); current = null; });
      on(el, 'pointerdown', function () { reset(current); current = null; });
    }

    function onMotionChange() {
      // prefers-reduced-motion flipped while the page is open: rebuild with the other settings, same brand
      var at = swiper ? swiper.activeIndex : api.index;
      swiper.destroy(true, true);
      swiper = null;
      build(at);
      root.setAttribute('data-bsw-state', 'in');
    }

    function destroy() {
      cleanups.forEach(function (fn) { fn(); });
      cleanups = [];
      if (swiper) { swiper.destroy(true, true); swiper = null; }
    }

    if (typeof window.Swiper !== 'function' || !slides.length) return api;
    if (reduced()) root.setAttribute('data-bsw-state', 'in');
    else root.setAttribute('data-bsw-state', 'pre');
    build(Math.max(0, Math.min(slides.length - 1, api.index)));
    wireInteraction();
    wireTilt();
    if (reducedQuery && reducedQuery.addEventListener) {
      reducedQuery.addEventListener('change', onMotionChange);
      cleanups.push(function () { reducedQuery.removeEventListener('change', onMotionChange); });
    }
    return api;
  }

  window.NcBrandSwiper = { mount: mount, tilt: tilt };
})();
